import { useEffect, useMemo, useState } from "react";

import { Control, FieldValues } from "react-hook-form";
import { debounce, isNil } from "lodash";

import { OptionType } from "@covertree/types";
import {
  CircularProgress,
  FilterOptionsState,
  Grid,
  useTheme,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import { FormAutocomplete } from "../FormAutocomplete";
import { SxProps } from "@mui/system";
import { InputBaseComponentProps } from "@mui/material/InputBase/InputBase";

const ASYNC_SELECT_DATA_FETCH_DEBOUNCE = 1000;

const filter = createFilterOptions<OptionType>();

const mapToOptionType = (arg: Array<string>) =>
  arg
    .filter((p) => !isNil(p))
    .map<OptionType>((param) => ({
      label: param,
      value: param,
    }));

interface SearchArgs {
  variables: {
    search: string;
  };
}

interface Props<TValues extends FieldValues> {
  id: string;
  label: string;
  dataTestId: string;
  placeholder: string;
  tooltip?: string;
  loading: boolean;
  name: string;
  defaultSearch?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  data?: Array<string>;
  initialData?: Array<string>;
  inputStyle?: SxProps;
  inputProps?: InputBaseComponentProps;
  control: Control<TValues>;
  withCustomValue?: boolean;
  disabled?: boolean;
  optional?: boolean;
  fontSize?: number;
  labelFontSize?: number;
  onGetAsyncData(arg: SearchArgs): void;
}

export function FormAsyncSelect<TValues extends FieldValues>({
  id,
  name,
  label,
  dataTestId,
  defaultSearch,
  placeholder,
  tooltip,
  data,
  initialData,
  control,
  loading,
  withCustomValue,
  disabled,
  onGetAsyncData,
  inputStyle,
  inputProps,
  optional = false,
  fontSize,
  labelFontSize,
}: Props<TValues>) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState<string>();

  const options = useMemo(() => {
    if (loading && inputValue && inputValue.length >= 3) {
      return [];
    }

    if ((!inputValue && !defaultSearch) || !data)
      return mapToOptionType(initialData ?? []);

    return mapToOptionType(data);
  }, [data, initialData, inputValue, loading, defaultSearch]);

  const callForData = useMemo(
    () =>
      debounce((input: string) => {
        if (input.length >= 3)
          onGetAsyncData({ variables: { search: input.toLowerCase() } });
      }, ASYNC_SELECT_DATA_FETCH_DEBOUNCE),
    [onGetAsyncData]
  );

  useEffect(() => {
    if (inputValue) {
      callForData(inputValue);
    }
  }, [callForData, inputValue]);

  useEffect(() => {
    if (defaultSearch) {
      // Match the typed-search path, which lowercases — the backend search is
      // case-sensitive, so an uppercase state prefix returns no options.
      onGetAsyncData({ variables: { search: defaultSearch.toLowerCase() } });
    }
    // Keyed on `defaultSearch` only: the prefetch fires on mount and whenever
    // the search becomes available/changes (e.g. once the policy/state loads).
    // `onGetAsyncData` is intentionally excluded — a constant `defaultSearch`
    // (e.g. ManufacturerSelect) then still fetches exactly once, unchanged from
    // before, and an unstable callback can't trigger a refetch loop.
  }, [defaultSearch]);

  return (
    <FormAutocomplete
      id={id}
      name={name}
      label={label}
      dataTestId={dataTestId}
      placeholder={placeholder}
      tooltip={tooltip}
      inputStyle={inputStyle}
      inputProps={inputProps}
      filterOptions={(
        opts: OptionType<string>[],
        params: FilterOptionsState<OptionType<string>>
      ) => {
        if (!withCustomValue) return opts;
        const filtered = filter(opts, params);

        if (params.inputValue.trim() !== "" && filtered.length === 0) {
          filtered.push({
            value: params.inputValue,
            label: params.inputValue,
          });
        }

        return filtered;
      }}
      onInputChange={(e) =>
        setInputValue((e as React.ChangeEvent<HTMLInputElement>).target.value)
      }
      onClose={() => setInputValue("")}
      loadingText={
        <Grid container alignContent="center" justifyContent="center">
          <CircularProgress
            color="secondary"
            data-testid={`${dataTestId}-loading`}
            size={theme.spacing(3.5)}
          />
        </Grid>
      }
      options={options}
      control={control}
      loading={loading}
      disabled={disabled}
      optional={optional}
      fontSize={fontSize}
      labelFontSize={labelFontSize}
      blurOnSelect
    />
  );
}
